import { useRef, useState } from 'react';
import type { JSX } from 'react';
import { ImagePlus } from 'lucide-react';
import type { TranslationKeys } from '../../i18n/types.js';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/** The image types the upload route accepts (server/src/routes/upload.router.ts). */
const ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

/** «my_cave.final.PNG» → «my cave final»: the name a map gets from its picture. */
export function mapNameFromFile(fileName: string): string {
  return fileName
    .replace(/\.[^.]+$/, '')
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface NewMapFormProps {
  readonly t: (key: keyof TranslationKeys) => string;
  readonly onSubmit: (name: string, image: File | undefined) => void;
  readonly onCancel: () => void;
}

/**
 * GK-3: «＋ Новая карта» is ONE window: the name and the picture. The name defaults to the file's (until the GM
 * types their own), the map is made and given the picture in one go, and the beat stays on the screen.
 */
export function NewMapForm({ t, onSubmit, onCancel }: NewMapFormProps): JSX.Element {
  const [name, setName] = useState('');
  const [touched, setTouched] = useState(false);
  const [image, setImage] = useState<File | undefined>(undefined);
  const fileRef = useRef<HTMLInputElement>(null);
  const ready = name.trim() !== '';
  const submit = (): void => {
    if (ready) onSubmit(name.trim(), image);
  };
  return (
    <form
      className="mt-1.5 flex flex-col gap-1.5 rounded-md border border-line bg-panel-2 px-2.5 py-2"
      data-testid="beat-maps-create-form"
      // What is typed here lives nowhere else until «Готово»: Tia's navigation does not replace the pane (P6).
      data-studio-unsaved={name.trim() !== '' || image !== undefined ? '' : undefined}
      onSubmit={(e): void => {
        e.preventDefault();
        submit();
      }}
      onKeyDown={(e): void => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onCancel();
        }
      }}
    >
      <Input
        autoFocus
        data-testid="beat-maps-create-name"
        aria-label={t('studio.maps.createPrompt')}
        placeholder={t('studio.maps.createPrompt')}
        value={name}
        onChange={(e): void => {
          setTouched(true);
          setName(e.target.value);
        }}
      />
      <div className="flex min-w-0 items-center gap-1.5">
        <Input
          ref={fileRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          data-testid="beat-maps-create-file"
          onChange={(e): void => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file === undefined) return;
            setImage(file);
            if (!touched || name.trim() === '') setName(mapNameFromFile(file.name));
          }}
        />
        <Button type="button" size="sm" variant="outline" className="flex-none" onClick={(): void => fileRef.current?.click()}>
          <ImagePlus size={14} aria-hidden="true" />
          {t(image === undefined ? 'studio.maps.createPick' : 'studio.maps.createChange')}
        </Button>
        <span className="min-w-0 truncate text-meta text-muted-foreground" data-testid="beat-maps-create-file-name" title={image?.name}>
          {image?.name ?? t('studio.maps.createNoImage')}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <Button type="submit" size="sm" disabled={!ready} data-testid="beat-maps-create-submit">
          {t('studio.maps.createSubmit')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel}>
          {t('inputDialog.cancel')}
        </Button>
      </div>
    </form>
  );
}
